import {
  PnoeMeasurementCatalogs,
  PnoeMeasurementListItem,
} from "../../entities/pnoeMeasurement";
import { enPrecisionTestKind, getProviderLabel } from "../../store/helper";
import { HistoryInbodyListItem, historyClientName } from "../../entities/history";
import {
  ResolvedStatus,
  resolveLabPanelStatus,
  StatusTone,
} from "../../utils/labPanelStatus";
import {
  getMeasurementTypeLabel,
  resolveCompositeMeasurementStatus,
} from "../../utils/pnoeMeasurementStatus";
import { formatPnoeDevice } from "../../utils/pnoeDeviceLabel";

/**
 * One row of the History → Assessments table.
 *
 * The design merges three sources that today have nothing in common — lab
 * panels (Platform API, one unpaginated POST), PNOĒ breath tests (a separate
 * cursor-paginated service) and InBody body-composition tests — into a single
 * table distinguished by a Type column. This is the shape they all normalise
 * to, so the table renders one kind of row and the filters and sort operate on
 * one set of fields.
 *
 * When the backend ships a unified assessments endpoint, only the adapters
 * below change; the table keeps consuming `AssessmentHistoryRow`.
 *
 * InBody rows come from the paged `/history/inbody` listing — one row per
 * imported batch of body-composition biomarkers (`inbodyToRow`).
 */
export type AssessmentKind = "lab" | "pnoe" | "inbody";

export interface AssessmentHistoryRow {
  /** Detail-route param. Lab rows use `panel.id`, PNOĒ rows `measurement.id`. */
  id: string;
  kind: AssessmentKind;
  /** ISO date; drives both the sort and the Date column. */
  date: string | null;
  /**
   * Lower-cased. The platform writes GUIDs in lower case and the breath
   * service in upper case; normalising here keeps one client one filter
   * option and lets the subject filter match across sources.
   */
  clientId: string | null;
  clientName: string;
  /** What was measured — the lab panel's name, "RMR", "Body composition". */
  detail: string;
  /** The Type column: "Lab panel" / "PNOE test" / "InBody test". */
  typeLabel: string;
  provider: string;
  device: string | null;
  /** Empty when the backend sent a status this build doesn't know — the badge
   *  is then omitted rather than rendered blank, as the Orders table did. */
  statusLabel: string;
  statusTone: StatusTone;
  /**
   * The affiliate has to act on this row: drives the colored left accent and
   * the hover "Review" tile, exactly as in the two listings this replaces.
   */
  actionRequired: boolean;
  /** Stable key for the Status column filter. */
  statusKey: string;
  /** The source record, handed to the detail views. */
  raw: any;
}

export const ASSESSMENT_TYPE_LABELS: Record<AssessmentKind, string> = {
  lab: "Lab panel",
  pnoe: "PNOE test",
  inbody: "InBody test",
};

/** GUIDs compare equal regardless of case; the sources disagree on it. */
export const normalizeClientId = (
  value: string | null | undefined
): string | null => (value ? String(value).toLowerCase() : null);

const clientName = (panel: any): string =>
  `${panel?.customerFirstName || ""} ${panel?.customerLastName || ""}`.trim();

const testKindLabel = (kind: number | null | undefined): string =>
  kind === null || kind === undefined ? "" : enPrecisionTestKind[kind] || "";

/** A lab panel / customer order → one Assessments row. */
export const labPanelToRow = (panel: any): AssessmentHistoryRow => {
  const resolved: ResolvedStatus | null = resolveLabPanelStatus(panel);
  return {
    id: String(panel?.id ?? ""),
    kind: "lab",
    date: panel?.dateCreated ?? null,
    clientId: normalizeClientId(panel?.customerID),
    clientName: clientName(panel),
    // The panel's own name; the kind only when a panel has none.
    detail: String(panel?.name ?? "").trim() || testKindLabel(panel?.kind),
    typeLabel: ASSESSMENT_TYPE_LABELS.lab,
    provider: getProviderLabel(panel?.provider),
    // Lab work has no device — the design shows an em dash in this column.
    device: null,
    statusLabel: resolved?.label ?? "",
    statusTone: resolved?.tone ?? "info",
    actionRequired: resolved?.actionRequired ?? false,
    statusKey: resolved?.key ?? "unknown",
    raw: panel,
  };
};

/**
 * A PNOĒ measurement → one Assessments row.
 *
 * The badge combines both statuses the way the design does ("Completed ·
 * Report ready") — the same resolver the detail page's header uses, so a test
 * reads identically in the listing and on its own page.
 */
export const pnoeMeasurementToRow = (
  measurement: PnoeMeasurementListItem,
  catalogs?: PnoeMeasurementCatalogs | null
): AssessmentHistoryRow => {
  const status = resolveCompositeMeasurementStatus(
    measurement.status,
    measurement.report_status,
    catalogs
  );

  return {
    id: measurement.id,
    kind: "pnoe",
    date: measurement.started_at,
    clientId: normalizeClientId(measurement.customer_id),
    clientName: measurement.customer_name || "",
    detail: getMeasurementTypeLabel(measurement.measurement_type, catalogs),
    typeLabel: ASSESSMENT_TYPE_LABELS.pnoe,
    provider: "PNOĒ",
    // "PNOĒ 1 · PNOE 2316-135": the service sends a serial and a generation,
    // no name. Older records have no generation and show the serial alone.
    device: formatPnoeDevice(measurement, catalogs),
    statusLabel: status?.label ?? "",
    statusTone: status?.tone ?? "info",
    actionRequired: status?.actionRequired ?? false,
    // Both halves, so filtering by "completed, report failed" is possible.
    statusKey: status?.key ?? "unknown",
    raw: measurement,
  };
};

/**
 * An InBody test (one imported batch) → one Assessments row.
 *
 * There is no status to resolve: a batch only exists once its results are
 * in, so every row reads "Completed · Approved", the wording the design
 * uses for a finished test. The Device column ("InBody 770S") is the one
 * thing that tells two tests apart besides the date.
 */
/** Every InBody test is a finished, accepted result. */
export const INBODY_STATUS_LABEL = "Completed · Approved";

export const inbodyToRow = (item: HistoryInbodyListItem): AssessmentHistoryRow => ({
  id: item.id,
  kind: "inbody",
  // When the test entered the platform, not when it was taken — the detail
  // page titles itself with the same date and shows the measurement date as
  // its own field.
  date: item.dateCreated ?? item.testDate ?? null,
  clientId: normalizeClientId(item.customerID),
  clientName: historyClientName(item),
  detail: enPrecisionTestKind[4] || "Body composition",
  typeLabel: ASSESSMENT_TYPE_LABELS.inbody,
  provider: "InBody",
  // "InBody 770S", as the detail page and the design name the device.
  device: item.device ? `InBody ${item.device}` : null,
  statusLabel: INBODY_STATUS_LABEL,
  statusTone: "success",
  actionRequired: false,
  statusKey: "inbody:completed-approved",
  raw: item,
});

/** Newest first; rows with no usable date sink to the bottom. */
export const sortAssessmentRows = (
  rows: AssessmentHistoryRow[]
): AssessmentHistoryRow[] => {
  const time = (row: AssessmentHistoryRow) => {
    if (!row.date) return 0;
    const parsed = new Date(row.date).getTime();
    return isNaN(parsed) ? 0 : parsed;
  };
  return [...rows].sort((a, b) => time(b) - time(a));
};
